import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useDeviceType, isPageRestricted } from "@/lib/device";
import { toast } from "sonner";
import { Shield, ShieldOff, LayoutDashboard, ClipboardEdit, ClipboardList, ClipboardCheck, History, Settings, LogOut, User, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Eye, ListChecks, BadgeCheck, Contact, LayoutList, ChartGantt } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => { setCollapsed(!!user?.hide_menu); }, [user?.hide_menu]);
  const isAdmin = user?.role === "admin" || !!user?.admin_level;
  const adminOn = user?.admin_mode !== false;
  const toggleAdminMode = async () => {
    try {
      await api.post("/auth/admin-mode", { on: !adminOn });
      await refresh();
      toast.success(!adminOn ? "Admin mode on — editing enabled" : "Admin mode off — editing disabled");
      if (adminOn && loc.pathname.startsWith("/admin")) nav("/");
    } catch (e) { toast.error("Could not change admin mode"); }
  };
  const stopImpersonation = async () => {
    try {
      await api.post("/auth/stop-impersonation");
      await refresh();
      nav("/admin");
      toast.success("Back to your own account");
    } catch (e) { toast.error("Could not return to your account"); }
  };
  const device = useDeviceType();
  const taskLabel = user?.terminology?.task_label || "Task";
  const tasksLabel = user?.terminology?.task_label_plural || "Tasks";
  const myPage = user?.my_page || {};
  const structure = [
    { id: "fatigue", label: "Fatigue", icon: ClipboardEdit, testId: "navgrp-fatigue", items: [
      { to: "/fatigue", label: "Daily Entry", icon: ClipboardEdit, testId: "nav-fatigue" },
      { to: "/", label: "Fatigue Board", icon: LayoutDashboard, testId: "nav-dashboard", module: "dashboard" },
      { to: "/history", label: "Fatigue History", icon: History, testId: "nav-history", module: "history" },
      { to: "/monthly", label: "Monthly View", icon: CalendarDays, testId: "nav-monthly", module: "monthly" },
    ]},
    { to: "/commitments", label: "Commitments", icon: CalendarDays, testId: "nav-commitments", module: "tasks" },
    { id: "tasks", label: tasksLabel, icon: ClipboardList, testId: "navgrp-tasks", module: "tasks", items: [
      { to: "/tasks", label: `${taskLabel} Planning`, icon: ClipboardList, testId: "nav-tasks" },
      { to: "/task-timeline", label: `${taskLabel} Timeline`, icon: ChartGantt, testId: "nav-task-timeline" },
      { to: "/tasks/list", label: `${taskLabel} List`, icon: LayoutList, testId: "nav-task-list" },
      { to: "/task-auth", label: `${taskLabel} Authorisation`, icon: BadgeCheck, testId: "nav-task-auth" },
      { to: "/post-task", label: `Post ${taskLabel}`, icon: ClipboardCheck, testId: "nav-post-task" },
      { to: "/task-history", label: `${taskLabel} History`, icon: ListChecks, testId: "nav-task-history", module: "task_history" },
    ]},
    { id: "profile", label: "Profile", icon: User, testId: "navgrp-profile", items: [
      ...(myPage.enabled !== false ? [{ to: "/my", label: myPage.name || "My Page", icon: Contact, testId: "nav-my-page" }] : []),
      { to: "/profile", label: "Profile Settings", icon: Settings, testId: "nav-profile" },
    ]},
  ];
  const visible = (it) => isAdmin || !it.module || !isPageRestricted(user, it.module, device);
  const menu = structure
    .map((g) => (g.items ? { ...g, items: g.items.filter(visible) } : g))
    .filter((g) => (g.items ? g.items.length > 0 : true) && visible(g));
  if (isAdmin && adminOn) menu.push({ to: "/admin", label: "Admin", icon: Settings, testId: "nav-admin" });
  const [openGroup, setOpenGroup] = useState(null);
  useEffect(() => {
    const g = menu.find((x) => x.items && x.items.some((it) => it.to === loc.pathname));
    setOpenGroup(g ? g.id : null);
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-950 text-slate-100">
      <aside className={`lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-950/80 backdrop-blur ${collapsed ? "lg:hidden" : ""}`}>
        <div className="p-5 flex items-center gap-2.5 border-b border-slate-800">
          <div className="h-9 w-9 grid place-items-center rounded-md bg-blue-600/20 border border-blue-500/40">
            <Shield className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold tracking-tight" data-testid="app-title">Crew Ready</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Risk · Rest · Readiness</div>
          </div>
          <button onClick={() => setCollapsed(true)} data-testid="sidebar-collapse" title="Hide menu"
            className="hidden lg:grid ml-auto h-6 w-6 place-items-center rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {menu.map((g) => {
            const Icon = g.icon;
            if (!g.items) {
              const active = loc.pathname === g.to;
              return (
                <Link
                  key={g.to}
                  to={g.to}
                  data-testid={g.testId}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                    active
                      ? "bg-blue-600/15 text-blue-300 border border-blue-600/30"
                      : "text-slate-300 hover:bg-slate-800/70 hover:text-white border border-transparent"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {g.label}
                </Link>
              );
            }
            const open = openGroup === g.id;
            const groupActive = g.items.some((it) => it.to === loc.pathname);
            return (
              <div key={g.id} className="flex lg:flex-col gap-1">
                <button onClick={() => { setOpenGroup(g.id); nav(g.items[0].to); }} data-testid={g.testId}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap lg:w-full ${
                    groupActive
                      ? "bg-blue-600/10 text-blue-300 border border-blue-600/25"
                      : "text-slate-300 hover:bg-slate-800/70 hover:text-white border border-transparent"
                  }`}>
                  <Icon className="h-4 w-4" />
                  {g.label}
                  <ChevronDown className={`h-3.5 w-3.5 lg:ml-auto transition-transform ${open ? "" : "-rotate-90"}`} />
                </button>
                {open && g.items.map((it) => {
                  const Sub = it.icon;
                  const active = loc.pathname === it.to;
                  return (
                    <Link
                      key={it.to}
                      to={it.to}
                      data-testid={it.testId}
                      className={`flex items-center gap-2.5 px-3 lg:pl-9 py-2 rounded-md text-[13px] transition-colors whitespace-nowrap ${
                        active
                          ? "bg-blue-600/15 text-blue-300 border border-blue-600/30"
                          : "text-slate-400 hover:bg-slate-800/70 hover:text-white border border-transparent"
                      }`}
                    >
                      <Sub className="h-3.5 w-3.5" />
                      {it.label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="mt-auto lg:absolute lg:bottom-0 lg:w-64 p-4 border-t border-slate-800 hidden lg:block">
          <div className="text-xs text-slate-500 font-mono-num uppercase tracking-wider mb-1">Signed in</div>
          <div className="text-sm text-slate-200 truncate" data-testid="current-user-name">
            {user?.first_name} {user?.last_name}
          </div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          {isAdmin && (
            <button
              onClick={toggleAdminMode}
              data-testid="admin-mode-toggle"
              title="Toggle admin editing privileges for yourself"
              className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm transition-colors border ${
                adminOn
                  ? "bg-blue-600/15 text-blue-300 border-blue-600/40 hover:bg-blue-600/25"
                  : "bg-slate-800/60 text-slate-400 border-slate-700 hover:text-slate-200"
              }`}
            >
              {adminOn ? <Shield className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
              Admin mode: {adminOn ? "On" : "Off"}
            </button>
          )}
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-200 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      {collapsed && (
        <button onClick={() => setCollapsed(false)} data-testid="sidebar-expand" title="Show menu"
          className="hidden lg:grid fixed left-3 top-3 z-30 h-9 w-9 place-items-center rounded-md bg-slate-900 border border-slate-700 text-slate-300 shadow-lg shadow-slate-950/60 hover:text-white hover:border-slate-500 hover:bg-slate-800 transition-colors">
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      <main className="flex-1 min-h-screen">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <span className="text-sm text-slate-300">{user?.first_name} {user?.last_name}</span>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={toggleAdminMode} data-testid="admin-mode-toggle-mobile"
                title="Toggle admin editing privileges for yourself"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
                  adminOn
                    ? "bg-blue-600/15 text-blue-300 border-blue-600/40"
                    : "bg-slate-800/60 text-slate-400 border-slate-700"
                }`}>
                {adminOn ? <Shield className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
                Admin: {adminOn ? "On" : "Off"}
              </button>
            )}
            <button data-testid="logout-btn-mobile" onClick={async () => { await logout(); nav("/login"); }}
              className="text-xs text-slate-400 hover:text-white">Sign out</button>
          </div>
        </div>
        <div className="p-5 sm:p-8 max-w-7xl mx-auto">
          {user?.impersonated_by && (
            <div data-testid="impersonation-banner"
              className="mb-5 flex items-center gap-3 flex-wrap rounded-md border border-amber-500/60 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300">
              <Eye className="h-4 w-4 shrink-0" />
              <span>
                Viewing Crew Ready as <strong>{user.first_name} {user.last_name}</strong> — changes you make apply to their data.
              </span>
              <button onClick={stopImpersonation} data-testid="impersonation-stop-btn"
                className="ml-auto px-3 py-1.5 rounded-md bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/60 text-xs font-semibold transition-colors">
                Return to my account
              </button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
